import type { UseFetchOptions } from 'nuxt/app'

export function useApi<T>(
  url: MaybeRef<string> | (() => MaybeRef<string>),
  options?: UseFetchOptions<T>
) {
  return useFetch(toValue(url), {
    ...options,
    $fetch: useNuxtApp().$api as typeof $fetch
  })
}

// Separate function for API calls that need proper error handling
export async function useApiCall<T>(url: string, options?: Record<string, unknown>): Promise<T> {
  const { $api } = useNuxtApp()
  return $api<T>(url, options as Record<string, unknown>) as Promise<T>
}

// A cancelled request surfaces as a FetchError whose cause is an AbortError
function isAbortError(error: { cause?: unknown } | null | undefined): boolean {
  const cause = error?.cause as { name?: string } | undefined

  return cause?.name === 'AbortError'
}

export async function useApiWith404<T>(
  url: MaybeRef<string> | (() => MaybeRef<string>),
  options?: UseFetchOptions<T>,
  errorMessage?: string
) {
  const route = useRoute()
  const pathBeforeRequest = route.fullPath

  const { data, error, status } = await useApi<T>(url, options)

  // A cancelled request is no evidence the resource is missing, so it must not
  // become a 404 — the user has already left the page anyway
  const navigatedAway = import.meta.client && route.fullPath !== pathBeforeRequest
  const wasCancelled = navigatedAway || isAbortError(error.value)

  if ((error.value || !data.value?.data) && !wasCancelled) {
    // Without statusMessage the error page falls back to a localized text
    showError({
      statusCode: error.value?.statusCode || 404,
      ...(errorMessage ? { statusMessage: errorMessage } : {})
    })
  }

  return { data, error, status }
}
